import { Link } from "react-router-dom";
import { Terminal, Key, Zap } from "lucide-react";

export function ApiPage() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-12 md:py-16 space-y-12">
      <div className="text-center space-y-3">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">DocLens API</h1>
        <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
          One REST endpoint. Authenticated with an API key. Predictable per-document pricing.
        </p>
        <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-900 text-xs font-medium px-3 py-1">
          <Zap className="h-3 w-3" /> API access available on Starter and above
        </div>
      </div>

      {/* Quick example */}
      <section className="space-y-3">
        <h2 className="font-semibold text-xl flex items-center gap-2">
          <Terminal className="h-5 w-5" /> Quick example
        </h2>
        <pre className="rounded-lg bg-neutral-900 text-neutral-100 p-4 text-xs overflow-x-auto font-mono">
{`curl https://api.doclens.app/v1/extract \\
  -H "Authorization: Bearer sk_live_..." \\
  -F file=@receipt.jpg \\
  -F schema=receipt`}
        </pre>
        <p className="text-sm text-neutral-600">
          Response: structured JSON matching the schema (see{" "}
          <Link to="/" className="underline">supported schemas</Link>) in 3–8 seconds.
        </p>
      </section>

      {/* Endpoints */}
      <section className="space-y-3">
        <h2 className="font-semibold text-xl">Endpoints</h2>
        <div className="rounded-xl border border-neutral-200 divide-y divide-neutral-200">
          <Endpoint
            method="POST"
            path="/v1/extract"
            body="Synchronous extraction. Returns the result inline."
          />
          <Endpoint
            method="POST"
            path="/v1/extract/batch"
            body="Up to 100 files per batch. Async — returns a batch_id."
            tier="Pro+"
          />
          <Endpoint
            method="GET"
            path="/v1/extractions/:id"
            body="Poll for async result."
          />
          <Endpoint
            method="GET"
            path="/v1/usage"
            body="Current month usage + plan limit."
          />
          <Endpoint
            method="POST"
            path="/v1/webhooks"
            body="Configure a webhook URL for async result delivery."
            tier="Pro+"
          />
        </div>
        <p className="text-xs text-neutral-500">
          Full OpenAPI spec available at <code className="bg-neutral-100 px-1 rounded">api.doclens.app/openapi.json</code>{" "}
          once you're on a paid plan.
        </p>
      </section>

      {/* Auth */}
      <section className="space-y-3">
        <h2 className="font-semibold text-xl flex items-center gap-2">
          <Key className="h-5 w-5" /> Authentication
        </h2>
        <p className="text-neutral-700 text-sm">
          API keys are issued from your dashboard once you're on a paid plan. Pass the key in the
          <code className="bg-neutral-100 px-1 rounded mx-1">Authorization</code>
          header as <code className="bg-neutral-100 px-1 rounded">Bearer sk_live_...</code>.
        </p>
        <p className="text-neutral-700 text-sm">
          Keys are shown <strong>once at creation</strong> and stored hashed at rest — DocLens cannot
          recover a lost key. Rotate keys from the dashboard at any time.
        </p>
      </section>

      {/* Rate limits */}
      <section className="space-y-3">
        <h2 className="font-semibold text-xl">Rate limits</h2>
        <div className="rounded-xl border border-neutral-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Plan</th>
                <th className="text-left px-4 py-2 font-medium">Requests / minute</th>
                <th className="text-left px-4 py-2 font-medium">Monthly documents</th>
                <th className="text-left px-4 py-2 font-medium">Overage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              <Row plan="Free" rate="—" docs="10" overage="—" />
              <Row plan="Starter" rate="10" docs="100" overage="$0.05/doc" />
              <Row plan="Pro" rate="60" docs="1,000" overage="$0.05/doc" />
              <Row plan="Business" rate="300" docs="10,000" overage="$0.05/doc" />
            </tbody>
          </table>
        </div>
      </section>

      {/* CTA */}
      <section className="rounded-2xl bg-neutral-900 text-white p-8 text-center space-y-4">
        <h2 className="text-2xl font-bold">Ready to integrate?</h2>
        <p className="text-neutral-300 max-w-md mx-auto text-sm">
          Sign up for the Starter or Pro plan to generate your first API key.
        </p>
        <Link
          to="/pricing"
          className="inline-block rounded-lg bg-brand text-white px-6 py-3 font-medium hover:bg-brand-700 transition"
        >
          See pricing
        </Link>
      </section>
    </div>
  );
}

function Endpoint({ method, path, body, tier }: { method: string; path: string; body: string; tier?: string }) {
  return (
    <div className="px-4 py-3 flex items-start gap-3">
      <span className="text-xs font-mono font-bold rounded bg-brand-50 text-brand-700 px-2 py-1 mt-0.5">
        {method}
      </span>
      <div className="flex-1">
        <code className="text-sm font-mono">{path}</code>
        <p className="text-xs text-neutral-600 mt-0.5">{body}</p>
      </div>
      {tier && <span className="text-xs text-neutral-500">{tier}</span>}
    </div>
  );
}

function Row({ plan, rate, docs, overage }: { plan: string; rate: string; docs: string; overage: string }) {
  return (
    <tr>
      <td className="px-4 py-2 font-medium">{plan}</td>
      <td className="px-4 py-2">{rate}</td>
      <td className="px-4 py-2">{docs}</td>
      <td className="px-4 py-2 text-neutral-600">{overage}</td>
    </tr>
  );
}
